import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Autocomplete,
  Breadcrumbs,
  Button,
  DataTable,
  InputField,
  SearchWithFilter,
  type DataTableColumn,
} from '@enamelai/design-system'
import { DeleteConfirmationModal } from '@components/layout/Modals'
import {
  addCarrierSettings,
  checkCarrierIfExists,
  deleteCarrierSettings,
  GetAllCarriers,
  getAllCarrierSettings,
  updateCarrierSettings,
} from '@services/api/carrier.api'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import { useAppSelector } from '@store/hooks'
import NoteTextEditor from '@components/common/NoteTextEditor/NoteTextEditor.component'
import { NoteContextType } from '@dataTypes/note.type'
import { logger } from '@utils/safeLogger'
import SettingsRowActions from '../../shared/SettingsRowActions.component'
import {
  sortRows,
  usePagedRows,
  useTableSort,
} from '../../shared/settingsTable'
import AddressFields, { type CarrierAddress } from './AddressFields.component'
import './CarriersStyle.css'

/** The shared notes editor is patient-scoped and never loads carrier notes. */
const SHOW_CARRIER_NOTES = false

const LOADING_CONTAINER = '.carriers-settings'
const WEBSITE_PROTOCOL = 'https://'
const MAX_PAYER_OPTIONS = 50

type CarrierRecord = {
  carrier_id: string
  carrier_name: string
  payer_id: string
}

type CarrierSetting = {
  carrier_setting_id: string
  carrier?: CarrierRecord | null
  address?: string | null
  city?: string | null
  state?: string | null
  zip?: string | null
  phone?: string | null
  fax?: string | null
  email?: string | null
  website?: string | null
}

type CarrierFormData = CarrierRecord &
  CarrierAddress & {
    phone: string
    fax: string
    email: string
    website: string
  }

type FormErrors = Partial<Record<keyof CarrierFormData, string>>

type CarrierRow = {
  id: string
  payerName: string
  payerId: string
  phone: string
  fax: string
  website: string
  feeScheduleId: string
}

const EMPTY_FORM: CarrierFormData = {
  carrier_id: '',
  carrier_name: '',
  payer_id: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  fax: '',
  email: '',
  website: '',
}

const EMPTY_CARRIER: CarrierRecord = {
  carrier_id: '',
  carrier_name: '',
  payer_id: '',
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const websiteRegex = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}$/

/** The DS phone field keeps 10 national digits, so drop a leading US country code. */
const digitsOf = (value: string): string => {
  const digits = value.replace(/\D/g, '')
  return digits.length === 11 && digits.startsWith('1')
    ? digits.slice(1)
    : digits
}

const stripProtocol = (url: string): string => url.replace(/^https?:\/\//i, '')

const toHref = (website: string): string =>
  /^https?:\/\//i.test(website) ? website : `${WEBSITE_PROTOCOL}${website}`

const sameText = (a: string, b: string): boolean =>
  a.trim().toLowerCase() === b.trim().toLowerCase()

const validateForm = (data: CarrierFormData): FormErrors => {
  const errors: FormErrors = {}
  if (!data.carrier_id || !data.carrier_name) {
    errors.carrier_name = 'Payer name is required'
  }
  if (!data.carrier_id || !data.payer_id) {
    errors.payer_id = 'Payer ID is required'
  }
  if (data.phone && digitsOf(data.phone).length !== 10) {
    errors.phone = 'Phone number must be exactly 10 digits'
  }
  if (data.fax && digitsOf(data.fax).length !== 10) {
    errors.fax = 'Fax number must be exactly 10 digits'
  }
  if (data.email && !emailRegex.test(data.email)) {
    errors.email = 'Invalid email address'
  }
  if (data.website && !websiteRegex.test(data.website)) {
    errors.website = 'Invalid website address'
  }
  return errors
}

const toPayerOptions = (
  carriers: CarrierRecord[],
  query: string,
  readLabel: (carrier: CarrierRecord) => string,
): { value: string; label: string }[] => {
  const needle = query.trim().toLowerCase()
  if (!needle) return []
  const seen = new Set<string>()
  const options: { value: string; label: string }[] = []
  for (const carrier of carriers) {
    const label = readLabel(carrier)
    if (!label || seen.has(label) || !label.toLowerCase().includes(needle)) {
      continue
    }
    seen.add(label)
    options.push({ value: carrier.carrier_id, label })
    if (options.length >= MAX_PAYER_OPTIONS) break
  }
  return options
}

const Carriers: React.FC = () => {
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const { setHeaderButtons, setHeaderTitle } = useSettingsHeader()

  const [carrierSettings, setCarrierSettings] = useState<CarrierSetting[]>([])
  const [carriers, setCarriers] = useState<CarrierRecord[]>([])
  const [listLoading, setListLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [isAddEditMode, setIsAddEditMode] = useState(false)
  const [formData, setFormData] = useState<CarrierFormData>(EMPTY_FORM)
  const [errors, setErrors] = useState<FormErrors>({})
  const [payerNameQuery, setPayerNameQuery] = useState('')
  const [payerIdQuery, setPayerIdQuery] = useState('')
  const [idToEdit, setIdToEdit] = useState<string | null>(null)
  const [rowToDelete, setRowToDelete] = useState<CarrierRow | null>(null)
  const [sort, toggleSort] = useTableSort('payerName')

  useSettingsSubpageLoading(listLoading)

  const fetchData = useCallback(async (): Promise<void> => {
    try {
      const settingsResponse = await getAllCarrierSettings()
      if (
        settingsResponse.statusCode === 200 &&
        'carrier_settings' in settingsResponse.data
      ) {
        setCarrierSettings(settingsResponse.data.carrier_settings)
      }
      const carriersResponse = await GetAllCarriers()
      if (
        carriersResponse.statusCode === 200 &&
        'insuranceProviders' in carriersResponse.data
      ) {
        setCarriers(carriersResponse.data.insuranceProviders)
      }
    } catch (error) {
      logger.error('Error loading carriers:', error)
    } finally {
      setListLoading(false)
    }
  }, [])

  const resetForm = useCallback((): void => {
    setFormData(EMPTY_FORM)
    setErrors({})
    setPayerNameQuery('')
    setPayerIdQuery('')
    setIdToEdit(null)
  }, [])

  const gotoListPage = useCallback((): void => {
    resetForm()
    void fetchData()
    setIsAddEditMode(false)
  }, [resetForm, fetchData])

  useEffect(() => {
    gotoListPage()
  }, [gotoListPage, selectedClinic])

  const rows: CarrierRow[] = useMemo(
    () =>
      carrierSettings.map((setting) => ({
        id: setting.carrier_setting_id,
        payerName: setting.carrier?.carrier_name ?? '',
        payerId: setting.carrier?.payer_id ?? '',
        phone: setting.phone ?? '',
        fax: setting.fax ?? '',
        website: setting.website ?? '',
        feeScheduleId: '',
      })),
    [carrierSettings],
  )

  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return rows
    const settingById = new Map(
      carrierSettings.map((setting) => [setting.carrier_setting_id, setting]),
    )
    return rows.filter((row) => {
      const setting = settingById.get(row.id)
      return [
        row.payerName,
        row.payerId,
        row.phone,
        row.fax,
        row.website,
        setting?.email,
        setting?.address,
        setting?.city,
        setting?.state,
        setting?.zip,
      ].some((field) => field?.toLowerCase().includes(query))
    })
  }, [rows, carrierSettings, searchQuery])

  const sortedRows = useMemo(
    () => sortRows(filteredRows, sort),
    [filteredRows, sort],
  )
  const { pageRows, pagination } = usePagedRows(sortedRows, {
    alwaysShow: true,
  })

  const payerNameOptions = useMemo(
    () => toPayerOptions(carriers, payerNameQuery, (c) => c.carrier_name),
    [carriers, payerNameQuery],
  )
  const payerIdOptions = useMemo(
    () => toPayerOptions(carriers, payerIdQuery, (c) => c.payer_id),
    [carriers, payerIdQuery],
  )

  const hasNoCarrierRecords = carrierSettings.length === 0

  const openAddCarrier = useCallback((): void => {
    resetForm()
    setIsAddEditMode(true)
  }, [resetForm])

  const cancelAddEdit = useCallback((): void => {
    resetForm()
    setIsAddEditMode(false)
  }, [resetForm])

  const handleEditClick = useCallback(
    (id: string): void => {
      const setting = carrierSettings.find(
        (item) => item.carrier_setting_id === id,
      )
      if (!setting) return
      const carrier = setting.carrier ?? EMPTY_CARRIER
      setIdToEdit(setting.carrier_setting_id)
      setErrors({})
      setFormData({
        carrier_id: carrier.carrier_id ?? '',
        carrier_name: carrier.carrier_name ?? '',
        payer_id: carrier.payer_id ?? '',
        address: setting.address ?? '',
        city: setting.city ?? '',
        state: setting.state ?? '',
        zip: setting.zip ?? '',
        phone: setting.phone ?? '',
        fax: setting.fax ?? '',
        email: setting.email ?? '',
        website: setting.website ?? '',
      })
      setPayerNameQuery(carrier.carrier_name ?? '')
      setPayerIdQuery(carrier.payer_id ?? '')
      setIsAddEditMode(true)
    },
    [carrierSettings],
  )

  const handleChange = (name: keyof CarrierFormData, value: string): void => {
    setFormData((prev) => ({ ...prev, [name]: value }))
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev))
  }

  const applyCarrier = (carrier: CarrierRecord | undefined): void => {
    setFormData((prev) => ({
      ...prev,
      ...(carrier
        ? {
            carrier_id: carrier.carrier_id,
            carrier_name: carrier.carrier_name,
            payer_id: carrier.payer_id,
          }
        : EMPTY_CARRIER),
    }))
    setErrors((prev) => ({
      ...prev,
      carrier_name: undefined,
      payer_id: undefined,
    }))
  }

  const handlePayerNameChange = (text: string): void => {
    setPayerNameQuery(text)
    const match = carriers.find((c) => sameText(c.carrier_name, text))
    if (match) {
      applyCarrier(match)
      setPayerIdQuery(match.payer_id)
    } else if (formData.carrier_id) {
      applyCarrier(undefined)
      setPayerIdQuery('')
    }
  }

  const handlePayerIdChange = (text: string): void => {
    setPayerIdQuery(text)
    const match = carriers.find((c) => sameText(c.payer_id, text))
    if (match) {
      applyCarrier(match)
      setPayerNameQuery(match.carrier_name)
    } else if (formData.carrier_id) {
      applyCarrier(undefined)
      setPayerNameQuery('')
    }
  }

  const handleSave = useCallback(async (): Promise<void> => {
    const nextErrors = validateForm(formData)
    setErrors(nextErrors)
    if (Object.values(nextErrors).some(Boolean)) return

    showLoading({ container: LOADING_CONTAINER })
    try {
      const carrierExists = await checkCarrierIfExists(
        formData.carrier_id,
        idToEdit ?? undefined,
      )
      if (carrierExists?.data?.result?.exists) {
        setTimeout(() => {
          hideLoading(LOADING_CONTAINER)
          showToast(
            createFailedToast(
              'Carrier already exists',
              'Carrier already exists. Please try again with a different carrier.',
            ),
          )
        }, 2000)
        return
      }

      const response = idToEdit
        ? await updateCarrierSettings(idToEdit, { ...formData })
        : await addCarrierSettings({ ...formData })

      setTimeout(() => {
        hideLoading(LOADING_CONTAINER)
        if (response.statusCode === 200) {
          showToast(
            createSuccessToast(
              idToEdit
                ? 'Carrier updated successfully'
                : 'Carrier added successfully',
              idToEdit
                ? 'Carrier has been updated successfully.'
                : 'Carrier has been added successfully.',
            ),
          )
          gotoListPage()
        } else {
          showToast(
            createFailedToast(
              idToEdit ? 'Carrier updating failed' : 'Carrier adding failed',
              idToEdit
                ? 'Carrier updating failed. Please try again later.'
                : 'Carrier adding failed. Please try again later.',
            ),
          )
        }
      }, 2000)
    } catch (error) {
      logger.error('Error saving carrier settings:', error)
      hideLoading(LOADING_CONTAINER)
      showToast(
        createFailedToast('Carrier save failed', 'Please try again later.'),
      )
    }
  }, [formData, idToEdit, showLoading, hideLoading, showToast, gotoListPage])

  const handleDeleteCarrier = useCallback(async (): Promise<void> => {
    if (!rowToDelete) return
    const failedToast = createFailedToast(
      'Carrier settings not deleted',
      'Carrier settings not deleted. Please try again.',
    )
    showLoading({ container: LOADING_CONTAINER })
    try {
      const response = await deleteCarrierSettings(rowToDelete.id)
      setTimeout(() => {
        hideLoading(LOADING_CONTAINER)
        if (response.statusCode === 200) {
          showToast(
            createSuccessToast(
              'Carrier settings deleted successfully',
              'Carrier settings have been deleted successfully.',
            ),
          )
          gotoListPage()
        } else {
          showToast(failedToast)
        }
      }, 1000)
    } catch (e) {
      logger.error('Error deleting carrier settings:', e)
      setTimeout(() => {
        hideLoading(LOADING_CONTAINER)
        showToast(failedToast)
      }, 1000)
    } finally {
      setRowToDelete(null)
    }
  }, [rowToDelete, showLoading, hideLoading, showToast, gotoListPage])

  useEffect(() => {
    if (!isAddEditMode) return
    setHeaderTitle(
      <Breadcrumbs
        className="carriers-breadcrumbs"
        showBackIcon
        onBackClick={cancelAddEdit}
        items={[
          { id: 'list', label: 'Carriers', onClick: cancelAddEdit },
          { id: 'current', label: idToEdit ? 'Edit carrier' : 'Add carrier' },
        ]}
      />,
    )
    return () => setHeaderTitle(null)
  }, [isAddEditMode, idToEdit, cancelAddEdit, setHeaderTitle])

  useEffect(() => {
    setHeaderButtons(
      isAddEditMode ? (
        <>
          <Button
            className="settings-ds-button"
            variant="secondary"
            size="md"
            type="button"
            onClick={cancelAddEdit}
          >
            Cancel
          </Button>
          <Button
            className="settings-ds-button"
            variant="primary"
            size="md"
            type="button"
            onClick={() => {
              void handleSave()
            }}
          >
            Save changes
          </Button>
        </>
      ) : (
        <>
          <SearchWithFilter
            className="carriers-search"
            placeholder="Search"
            value={searchQuery}
            onChange={setSearchQuery}
            filters={{}}
            showFilter={false}
          />
          {!hasNoCarrierRecords && (
            <Button
              className="settings-ds-button"
              variant="primary"
              size="md"
              type="button"
              onClick={openAddCarrier}
            >
              Add carrier
            </Button>
          )}
        </>
      ),
    )
    return () => setHeaderButtons(null)
  }, [
    isAddEditMode,
    cancelAddEdit,
    handleSave,
    searchQuery,
    hasNoCarrierRecords,
    openAddCarrier,
    setHeaderButtons,
  ])

  const columns: DataTableColumn<CarrierRow>[] = useMemo(
    () => [
      { id: 'payerName', label: 'Payer name', sortable: true, showInfo: true },
      {
        id: 'payerId',
        label: 'Payer ID',
        sortable: true,
        showInfo: true,
        width: 128,
        renderCell: (row) => row.payerId || 'N/A',
      },
      {
        id: 'phone',
        label: 'Contact no.',
        sortable: true,
        width: 136,
        renderCell: (row) => row.phone || 'N/A',
      },
      {
        id: 'fax',
        label: 'Fax',
        sortable: true,
        width: 136,
        renderCell: (row) => row.fax || 'N/A',
      },
      {
        id: 'website',
        label: 'Website',
        sortable: true,
        width: 96,
        renderCell: (row) =>
          row.website ? (
            <a
              className="carriers-website-link"
              href={toHref(row.website)}
              target="_blank"
              rel="noopener noreferrer"
              title={row.website}
            >
              Visit
            </a>
          ) : (
            'N/A'
          ),
      },
      {
        id: 'feeScheduleId',
        label: 'Fee schedule ID',
        sortable: true,
        width: 168,
        renderCell: (row) => row.feeScheduleId || 'N/A',
      },
    ],
    [],
  )

  const tableState = listLoading
    ? 'loading'
    : filteredRows.length === 0
      ? 'empty'
      : 'default'

  return (
    <div className="carriers-settings">
      {isAddEditMode ? (
        <form
          className="carriers-form"
          onSubmit={(e) => {
            e.preventDefault()
            void handleSave()
          }}
        >
          <section className="carriers-section">
            <h2 className="carriers-section-title">Enter carrier details</h2>
            <div className="carriers-form-row carriers-form-row--halves">
              <Autocomplete
                label="Payer name"
                placeholder="Search payer name"
                options={payerNameOptions}
                value={payerNameQuery}
                error={!!errors.carrier_name}
                helperText={errors.carrier_name}
                onChange={handlePayerNameChange}
              />
              <Autocomplete
                label="Payer ID"
                placeholder="Search payer ID"
                options={payerIdOptions}
                value={payerIdQuery}
                error={!!errors.payer_id}
                helperText={errors.payer_id}
                onChange={handlePayerIdChange}
              />
            </div>
          </section>

          <section className="carriers-section">
            <h2 className="carriers-section-title">Contact information</h2>
            <div className="carriers-form-row carriers-form-row--thirds">
              <InputField
                fullWidth
                variant="phone"
                label="Phone number"
                value={digitsOf(formData.phone)}
                error={!!errors.phone}
                helperText={errors.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
              />
              <InputField
                fullWidth
                variant="phone"
                label="Fax number"
                value={digitsOf(formData.fax)}
                error={!!errors.fax}
                helperText={errors.fax}
                onChange={(e) => handleChange('fax', e.target.value)}
              />
              <InputField
                fullWidth
                variant="email"
                label="Email"
                placeholder="example@email.com"
                value={formData.email}
                error={!!errors.email}
                helperText={errors.email}
                onChange={(e) => handleChange('email', e.target.value)}
              />
            </div>
            <InputField
              fullWidth
              variant="text"
              label="Website"
              placeholder="www.example.com"
              leadingIcon={
                <span className="carriers-url-prefix">{WEBSITE_PROTOCOL}</span>
              }
              value={stripProtocol(formData.website)}
              error={!!errors.website}
              helperText={errors.website}
              onChange={(e) => {
                const host = stripProtocol(e.target.value.trim())
                handleChange(
                  'website',
                  host ? `${WEBSITE_PROTOCOL}${host}` : '',
                )
              }}
            />
          </section>

          <section className="carriers-section">
            <h2 className="carriers-section-title">Address</h2>
            <AddressFields
              value={formData}
              onChange={(field, value) => handleChange(field, value)}
            />
          </section>

          {SHOW_CARRIER_NOTES && (
            <section className="carriers-section carriers-notes">
              <NoteTextEditor
                patientId=""
                notesOverlayPortalTarget="body"
                savedNotesAllowDelete={false}
                savedNotesUsePdfDownload
                noteData={{ tag: NoteContextType.CARRIER }}
                notes={[]}
              />
            </section>
          )}
        </form>
      ) : (
        <DataTable<CarrierRow>
          className="carriers-table"
          columns={columns}
          rows={pageRows}
          state={tableState}
          emptyState={{
            title: 'No carriers found',
            description: hasNoCarrierRecords
              ? "You don't have any carriers on record yet."
              : 'No carriers match your search.',
            actionLabel: hasNoCarrierRecords ? 'Add carrier' : undefined,
            onAction: hasNoCarrierRecords ? openAddCarrier : undefined,
          }}
          sortColumnId={sort.columnId}
          sortDirection={sort.columnId ? sort.direction : undefined}
          onSort={toggleSort}
          actionsLabel="Actions"
          renderActions={(row) => (
            <SettingsRowActions
              onEdit={() => handleEditClick(row.id)}
              onDelete={() => setRowToDelete(row)}
            />
          )}
          pagination={pagination}
        />
      )}
      <DeleteConfirmationModal
        show={!!rowToDelete}
        onHide={() => setRowToDelete(null)}
        onConfirm={() => {
          void handleDeleteCarrier()
        }}
        entityName={rowToDelete?.payerName ?? ''}
        title="Delete carrier?"
        message="Are you sure you want to delete the carrier"
        confirmButtonText="Delete"
      />
    </div>
  )
}

export default Carriers
